import type { InsufficientCreditsError, ValidationErrorResponse } from "api";
import axios from "axios";

const UNAUTHENTICATED = 401;
const FORBIDDEN = 403;
const NOT_FOUND = 404;
const UNPROCESSABLE = 422;
const NOT_IMPLEMENTED = 501;

const statusOf = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/** The session cookie is gone or expired: the user has to sign in again. */
export const isUnauthenticated = (error: unknown): boolean => statusOf(error) === UNAUTHENTICATED;

/** Signed in, but the account has not verified its email address. */
export const isForbidden = (error: unknown): boolean => statusOf(error) === FORBIDDEN;

/** The key is not there any more — revoked from another tab, most likely. */
export const isNotFound = (error: unknown): boolean => statusOf(error) === NOT_FOUND;

/**
 * The route is not there. The billing endpoints beyond `/api/billing/usage`
 * are still being built in the back office; their callers read this as "not
 * available yet" and keep the rest of the pane working.
 */
export const isUnavailable = (error: unknown): boolean => {
  const status = statusOf(error);

  return status === NOT_FOUND || status === NOT_IMPLEMENTED;
};

/**
 * 402: the action costs more QCredits than the company has. Recognised by
 * `code`, not by status: the `/api/tools/*` proxy also passes on the gateway's
 * own errors, and those carry no code; they are API limits, a different product.
 */
export const getCreditDenial = (error: unknown): InsufficientCreditsError | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const data: unknown = error.response?.data;
  if (typeof data !== "object" || data === null || !("code" in data)) {
    return null;
  }

  return data.code === "insufficient_credits" ? (data as InsufficientCreditsError) : null;
};

export const isInsufficientCredits = (error: unknown): boolean => getCreditDenial(error) !== null;

/**
 * Only a 422 carries messages meant for a human: they come from this API's own
 * validation rules. Every other status returns whatever the framework raised —
 * a 404 reads "No query results for model [Laravel\Sanctum\PersonalAccessToken]
 * 10", which is meaningless to the user and names our internals. Those get the
 * caller's fallback instead.
 */
export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (statusOf(error) !== UNPROCESSABLE) {
    return fallback;
  }

  const payload = (error as { response?: { data?: ValidationErrorResponse } }).response?.data;
  const firstFieldError = Object.values(payload?.errors ?? {})[0]?.[0];

  return firstFieldError ?? payload?.message ?? fallback;
};
